import { ref, computed, inject, onMounted, onUnmounted } from 'vue';
import { BOARD_NOW_KEY } from './useWaitClock.js';

/**
 * useMetaWindow — a janela da Meta de um card (2026-08-06).
 *
 * O que ela responde é uma pergunta de DINHEIRO, não de atraso: enquanto a janela está
 * aberta, responder ao cliente é grátis; fechada, só template pago alcança aquele número. A
 * janela reabre a cada mensagem do cliente — não é "24h para o lead existir", é "24h desde a
 * última vez que ele falou".
 *
 * Substitui o useSlaIndicator na leitura do card e do painel. Os dois liam o MESMO carimbo
 * (última mensagem do cliente + 24h), mas o rótulo antigo dizia "SLA estourado", que se lê
 * como "você está atrasado" — quando o fato é "responder agora custa". Rótulo errado sobre
 * dinheiro ensina a operação a ignorar o indicador.
 *
 * Estados possíveis:
 *   - semJanela: o canal não tem janela (site, e-mail, caixa de API sem
 *     agent_reply_time_window). A tela não mostra NADA: inventar "aberta" onde não existe
 *     janela ensinaria a regra errada.
 *   - naoAbriu: nunca houve mensagem do cliente. Diferente de "fechou".
 *   - aberta: "janela: 4h" — quanto falta para fechar.
 *   - fechada: "janela fechada" — responder exige template pago.
 *
 * Recalcula com o tick compartilhado do board (BOARD_NOW_KEY): um setInterval para o board
 * inteiro, não um por card. Fora do board cai num timer local.
 *
 * @param {(() => object|null)|import('vue').Ref} source getter/ref do objeto `janela` do payload
 */
export function useMetaWindow(source) {
  const injectedNow = inject(BOARD_NOW_KEY, null);
  const localNow = ref(Date.now());
  let fallbackTimer = null;

  if (!injectedNow) {
    onMounted(() => {
      fallbackTimer = setInterval(() => {
        localNow.value = Date.now();
      }, 1000);
    });
    onUnmounted(() => {
      if (fallbackTimer) clearInterval(fallbackTimer);
    });
  }

  const now = injectedNow ?? localNow;

  const resolve = () =>
    typeof source === 'function' ? source() : source?.value;

  // Tempo restante compacto pt-BR. Espelha o formato do useSlaIndicator para o board não
  // ter duas gramáticas de tempo lado a lado.
  const formatRemaining = ms => {
    const totalMin = Math.floor(ms / 60_000);
    if (totalMin < 1) return '<1min';
    if (totalMin < 60) return `${totalMin}min`;
    const totalHours = Math.floor(totalMin / 60);
    if (totalHours < 24) return `${totalHours}h`;
    return `${Math.floor(totalHours / 24)}d`;
  };

  const state = computed(() => {
    const janela = resolve();
    const vazio = {
      mostrar: false,
      aberta: false,
      fechada: false,
      label: null,
      titulo: null,
      fracao: null,
    };
    if (!janela || janela.sem_janela) return vazio;

    if (!janela.fecha_em) {
      return {
        mostrar: true,
        aberta: false,
        fechada: false,
        // ⚠️ O RÓTULO PRECISA SE EXPLICAR SOZINHO (2026-08-23). Ele era "sem janela aberta", depois
        // "sem janela", e o Gustavo perguntou: "o que seria sem janela?". Se o dono do produto não
        // sabe, a operadora não tem chance — e o custo é real, porque é essa a informação que diz
        // que responder aquele lead SAI CARO (fora das 24h do WhatsApp, só por modelo aprovado).
        // "fora das 24h" nomeia a regra que a pessoa já conhece do WhatsApp, em vez de nomear o
        // conceito interno ("janela") que só existe na nossa cabeça.
        label: 'fora das 24h',
        titulo:
          'O cliente ainda não enviou nenhuma mensagem — a janela gratuita nunca abriu.',
        // `null` e não `0`: nunca abriu é diferente de fechou. Zero seria "acabou o
        // tempo"; aqui não houve tempo nenhum, e a barra do card lê essa diferença.
        fracao: null,
      };
    }

    const fechaEm = new Date(janela.fecha_em);
    if (Number.isNaN(fechaEm.getTime())) return vazio;

    const restante = fechaEm.getTime() - now.value;
    if (restante <= 0) {
      return {
        mostrar: true,
        aberta: false,
        fechada: true,
        label: 'janela fechada',
        titulo:
          'A janela gratuita fechou. Responder agora exige mensagem de template, que é paga. ' +
          'Ela reabre assim que o cliente escrever de novo.',
        fracao: 0,
      };
    }
    // FRAÇÃO DO QUE SOBRA (2026-08-08) — nasceu para a barra de prazo do card.
    //
    // Vive AQUI e não no card por um motivo que este arquivo já aprendeu: o `label` e a
    // barra têm de contar a mesma coisa. Um segundo leitor da janela seria um segundo
    // número para "quanto falta", e ninguém saberia qual acreditar — foi exatamente por
    // isso que o `useSlaIndicator` deixou de ser lido pelo card.
    //
    // `horas` vem do CANAL (24h no WhatsApp, 48h no TikTok, configurável em caixa de API),
    // então a barra de um card de TikTok esvazia mais devagar que a de um de WhatsApp — e
    // está certo: são janelas de tamanhos diferentes.
    const totalMs = Number(janela.horas) * 3_600_000;
    const fracao =
      Number.isFinite(totalMs) && totalMs > 0
        ? Math.min(1, Math.max(0, restante / totalMs))
        : null;
    return {
      mostrar: true,
      aberta: true,
      fechada: false,
      label: `janela: ${formatRemaining(restante)}`,
      titulo: `Responder é gratuito por mais ${formatRemaining(restante)} (janela de ${janela.horas}h desde a última mensagem do cliente).`,
      fracao,
    };
  });

  return {
    mostrar: computed(() => state.value.mostrar),
    aberta: computed(() => state.value.aberta),
    fechada: computed(() => state.value.fechada),
    label: computed(() => state.value.label),
    titulo: computed(() => state.value.titulo),
    fracao: computed(() => state.value.fracao),
  };
}
